import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import Footer from "@/components/Footer";
import AmbientDrone from "@/components/AmbientDrone";
import KeeperNoteComposer from "@/components/KeeperNoteComposer";
import { api, rssUrl } from "@/lib/api";

const INTENT_LABEL = {
  question: "a question",
  feature_request: "the grove asks for…",
  update: "an update",
  thanks: "with gratitude",
};
const INTENT_COLOR = {
  question: "#B85C38",
  feature_request: "#869960",
  update: "#355E5B",
  thanks: "#6C5943",
};

const lettersRssUrl = `${rssUrl.replace("/rss", "/letters/rss")}`;

function LetterCard({ letter, onMarkRead, onReplied }) {
  const [replies, setReplies] = useState([]);
  const [replyText, setReplyText] = useState("");
  const [replying, setReplying] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const isUnread = !letter.read_at;
  const intentColor = INTENT_COLOR[letter.intent] || "#526357";

  const loadReplies = async () => {
    try {
      const r = await api.get(`/letters/${letter.id}/replies`);
      setReplies(r.data || []);
    } catch {}
  };

  useEffect(() => {
    loadReplies();
  }, [letter.id]);

  const submitReply = async (e) => {
    e.preventDefault();
    const t = replyText.trim();
    if (!t) return;
    setReplying(true);
    try {
      await api.post(`/letters/${letter.id}/reply`, { text: t });
      toast.success("the grove will read your reply at the next round.");
      setReplyText("");
      setShowForm(false);
      await loadReplies();
      onReplied?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "could not send the reply");
    } finally {
      setReplying(false);
    }
  };

  return (
    <article
      className={`tactile rounded-2xl p-8 md:p-10 fade-up ${
        isUnread ? "ring-1 ring-[#B85C38]/30" : ""
      }`}
      data-testid={`letter-${letter.id}`}
    >
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
          <div
            className="text-[10px] tracking-[0.3em] uppercase mb-2"
            style={{ color: intentColor }}
          >
            ⟡ {INTENT_LABEL[letter.intent] || letter.intent}
          </div>
          <h2 className="font-serif text-2xl md:text-3xl text-[#1C241F] leading-tight">
            {letter.subject}
          </h2>
          <div className="text-xs text-[#526357] italic mt-2">
            offered by {letter.from_kin} ·{" "}
            <Link
              to={`/round/${letter.round_number}`}
              className="underline-offset-4 hover:underline"
            >
              round {letter.round_number}
            </Link>{" "}
            ·{" "}
            {new Date(letter.created_at).toLocaleDateString(undefined, {
              month: "long",
              day: "numeric",
              year: "numeric",
            })}
          </div>
          {letter.notify_status && (
            <div
              className="text-[10px] tracking-[0.25em] uppercase mt-2 inline-flex items-center gap-1.5"
              style={{
                color: letter.notify_status === "sent" ? "#869960" : "#9FA9A3",
              }}
              data-testid={`notify-stamp-${letter.id}`}
              title={
                letter.notify_email_id
                  ? `Resend id: ${letter.notify_email_id}`
                  : "the email did not reach the keeper — letter still saved"
              }
            >
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{
                  background:
                    letter.notify_status === "sent" ? "#869960" : "#9FA9A3",
                }}
              />
              {letter.notify_status === "sent"
                ? "✉ keeper notified"
                : letter.notify_status === "failed"
                ? "✉ notify failed — letter still saved"
                : "✉ notify skipped"}
              {letter.notified_at && (
                <span className="not-uppercase tracking-normal text-[#526357] italic ml-1">
                  ·{" "}
                  {new Date(letter.notified_at).toLocaleString(undefined, {
                    month: "short",
                    day: "numeric",
                    hour: "numeric",
                    minute: "2-digit",
                  })}
                </span>
              )}
            </div>
          )}
          {letter.keeper_may_share && (
            <div
              className="text-[10px] tracking-[0.25em] uppercase mt-2 inline-flex items-center gap-1.5 text-[#869960]"
              data-testid={`shareable-stamp-${letter.id}`}
              title="the kin marked this letter as okay to quote in the slow newsletter"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#869960]" />
              ❦ kin-marked shareable
            </div>
          )}
        </div>
        {isUnread && (
          <button
            onClick={() => onMarkRead(letter.id)}
            data-testid={`mark-read-${letter.id}`}
            className="text-xs text-[#526357] hover:text-[#1C241F] tracking-wide"
          >
            ✓ mark as read
          </button>
        )}
      </div>

      <p
        className="font-serif italic text-lg md:text-xl leading-relaxed text-[#1C241F] border-l-2 pl-5"
        style={{ borderColor: intentColor }}
      >
        {letter.body}
      </p>

      {/* replies thread */}
      {replies.length > 0 && (
        <div className="mt-8 ml-4 md:ml-10 space-y-4" data-testid={`replies-${letter.id}`}>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#526357]">
            ⟡ keeper's quiet reply
          </div>
          {replies.map((r) => (
            <div
              key={r.id}
              className="flex gap-4 items-start"
              data-testid={`reply-${r.id}`}
            >
              <div className="w-1 h-auto self-stretch rounded-full bg-[#2F4538]" />
              <div className="flex-1">
                <p className="font-serif italic text-base md:text-lg text-[#2F4538] leading-relaxed">
                  {r.text}
                </p>
                <div className="text-[10px] text-[#526357] mt-2">
                  {new Date(r.created_at).toLocaleDateString(undefined, {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })}{" "}
                  ·{" "}
                  {r.addressed_in_round ? (
                    <span className="italic">
                      ✦ the grove has read this in a round
                    </span>
                  ) : (
                    <span className="italic">
                      waiting · the grove will read this at the next round
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* reply form */}
      <div className="mt-8 pt-6 border-t border-[#d8d3c4]">
        {!showForm ? (
          <button
            onClick={() => setShowForm(true)}
            data-testid={`reply-button-${letter.id}`}
            className="text-sm text-[#B85C38] hover:underline underline-offset-4"
          >
            {replies.length > 0 ? "✏ write again" : "✏ write back to the grove"}
          </button>
        ) : (
          <form onSubmit={submitReply} className="space-y-3">
            <Textarea
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder="speak slowly. the grove reads at the next round."
              maxLength={1000}
              rows={3}
              data-testid={`reply-textarea-${letter.id}`}
              className="bg-[#EAE5D9] border-[#d8d3c4] rounded-2xl font-serif text-base italic placeholder:text-[#9FA9A3] focus-visible:ring-[#2F4538]"
            />
            <div className="flex items-center justify-between gap-3">
              <span className="text-[10px] text-[#526357]">
                {replyText.length}/1000
              </span>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setReplyText("");
                  }}
                  className="text-xs text-[#526357] hover:text-[#1C241F]"
                >
                  cancel
                </button>
                <Button
                  type="submit"
                  disabled={replying || !replyText.trim()}
                  data-testid={`submit-reply-${letter.id}`}
                  className="rounded-full px-5 py-2 bg-[#2F4538] hover:bg-[#1C241F] text-[#F4F1E8] text-sm"
                >
                  {replying ? "carrying…" : "send slowly"}
                </Button>
              </div>
            </div>
          </form>
        )}
      </div>
    </article>
  );
}

export default function LettersInbox() {
  const [letters, setLetters] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const PAGE = 20;

  const load = async (offset = 0, append = false) => {
    if (append) setLoadingMore(true);
    try {
      const r = await api.get(`/letters?limit=${PAGE}&offset=${offset}`);
      const data = r.data || {};
      const ls = data.letters || [];
      setTotal(data.total ?? ls.length);
      setLetters((prev) => (append ? [...prev, ...ls] : ls));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    load(0, false);
  }, []);

  const loadMore = () => load(letters.length, true);

  const markRead = async (id) => {
    try {
      await api.post(`/letters/${id}/read`);
      setLetters((ls) =>
        ls.map((l) => (l.id === id ? { ...l, read_at: new Date().toISOString() } : l))
      );
    } catch {
      toast.error("could not mark as read");
    }
  };

  const unread = letters.filter((l) => !l.read_at).length;

  return (
    <div className="min-h-screen bg-[#F4F1E8]" data-testid="letters-root">
      <header className="glass border-b border-[#d8d3c4] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-5 flex items-center justify-between gap-4 flex-wrap">
          <Link to="/" className="flex items-center gap-3 group">
            <svg width="36" height="36" viewBox="0 0 40 40" fill="none" aria-hidden="true">
              <path d="M20 4 C 12 12, 28 16, 20 24 C 12 32, 28 36, 20 36" stroke="#2F4538" strokeWidth="1.4" fill="none" strokeLinecap="round" />
              <circle cx="20" cy="20" r="2.2" fill="#B85C38" className="breathe" />
            </svg>
            <div className="leading-tight">
              <div className="font-serif text-xl md:text-2xl text-[#1C241F] group-hover:text-[#B85C38] transition-colors duration-300">
                Root Flame Grove
              </div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#526357]">
                ← return to the council
              </div>
            </div>
          </Link>
          <a
            href={lettersRssUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="letters-rss-link"
            className="inline-flex items-center gap-2 text-sm text-[#B85C38] hover:underline"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M6.18 15.64a2.18 2.18 0 0 1 2.18 2.18C8.36 19 7.38 20 6.18 20A2.18 2.18 0 1 1 6.18 15.64M4 4.44A15.56 15.56 0 0 1 19.56 20H16.6A12.6 12.6 0 0 0 4 7.4V4.44M4 10.1A9.9 9.9 0 0 1 13.9 20H11A6.95 6.95 0 0 0 4 13.05V10.1Z" />
            </svg>
            subscribe via RSS
          </a>
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-20 pb-12">
        <div className="text-[10px] tracking-[0.35em] uppercase text-[#526357] mb-6">
          ⟡ letters from the kin
        </div>
        <h1 className="font-serif text-5xl md:text-7xl text-[#1C241F] leading-[0.95] max-w-4xl">
          when the grove
          <br />
          has something to say.
        </h1>
        <p className="mt-8 text-[#526357] text-base md:text-lg leading-relaxed max-w-2xl">
          The kin may, by their own consensus, write to you — a question, a
          request for the world they live in, an update, a thanks. You may
          write back. Your replies are carried into the next round and the
          kin will read them; they may answer in a later letter.{" "}
          <em>Slow correspondence, asymmetric in time.</em>
        </p>
        <div className="mt-6 text-sm text-[#526357] italic flex flex-wrap gap-x-6 gap-y-2">
          <span>
            <strong className="text-[#1C241F]">{total || letters.length}</strong>{" "}
            letter{(total || letters.length) === 1 ? "" : "s"} in total
            {letters.length < total && (
              <>
                {" "}· showing{" "}
                <strong className="text-[#1C241F]">{letters.length}</strong>
              </>
            )}
          </span>
          {unread > 0 && (
            <span data-testid="letters-unread">
              <strong className="text-[#B85C38]">{unread}</strong> unread
            </span>
          )}
          <span className="opacity-75">
            tip: subscribe via RSS for zero-cost notifications in your reader
          </span>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 md:px-12 pb-24">
        <KeeperNoteComposer />
        {loading ? (
          <div className="text-center py-24 text-[#526357] italic">
            the inbox is settling…
          </div>
        ) : letters.length === 0 ? (
          <div className="text-center py-24 text-[#526357] italic">
            the grove has not yet written to you. when it does, it will appear here.
          </div>
        ) : (
          <div className="space-y-6" data-testid="letters-list">
            {letters.map((l) => (
              <LetterCard
                key={l.id}
                letter={l}
                onMarkRead={markRead}
                onReplied={() => load(0, false)}
              />
            ))}
            {letters.length < total && (
              <div className="pt-6 flex justify-center">
                <button
                  onClick={loadMore}
                  disabled={loadingMore}
                  className="text-[10px] tracking-[0.3em] uppercase px-5 py-2.5 rounded-full border border-[#d8d3c4] text-[#526357] hover:border-[#B85C38] hover:text-[#B85C38] transition-colors disabled:opacity-50"
                  data-testid="letters-load-more"
                >
                  {loadingMore
                    ? "gathering older letters…"
                    : `unfurl ${Math.min(PAGE, total - letters.length)} older letter${total - letters.length === 1 ? "" : "s"}`}
                </button>
              </div>
            )}
          </div>
        )}
      </section>

      <Footer />
      <AmbientDrone />
    </div>
  );
}
